import {
  AttachMoney,
  Chat as ChatIcon,
  Event,
  Home,
  LocationCity,
  LocationOn,
  Pets,
  Videocam,
} from "@mui/icons-material";
import ChatMedPaciente from "@/components/videoConferencia/ChatMedPaciente";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { CalendarIcon } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { useRelatedPersons } from "@/hooks/session/useRelatedPersons";
import { GetAppointment, PatchAppointmentStatus } from "@/services/fhir";
import { GetPaymentNotice } from "@/services/fhir/paymentnotice/GetPaymentNotice";
import { PutPaymentNotice } from "@/services/fhir/paymentnotice/PutPaymentNotice";
import { DeleteWhereby } from "@/services/whereby/DeleteWhereby";
import { useDeleteEvent } from "@/services/google/DeleteEvent";
import { useFhirData } from "@/utils/useFhirData";
import { useDatetime } from "@/utils/useDatetime";
import { useNavigate } from "react-router-dom";
import VideoConferencia from "@/components/videoConferencia/VideoConferencia";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";

/** Tipos de atendimento no mesmo modelo de PatientDetails.tsx + codes explícitos da Location (AuxiliarData). */
type VetAttendanceKind =
  | "presencial"
  | "teleatendimento"
  | "chat"
  | "domicilio";

/**
 * Detecta o tipo de agenda a partir da Location (extension attendance-channel + virtual-service legado).
 * Mantém paridade com `getAttendanceKind` em PatientDetails.tsx, incluindo codes `presencial` e `teleatendimento`.
 */
function getVetAttendanceKind(locationResource: any): VetAttendanceKind {
  const channel = locationResource?.extension?.find((ext: any) =>
    ext.url?.includes("attendance-channel")
  )?.valueCode;

  const isVirtual =
    locationResource?.extension?.find((ext: any) =>
      ext.url?.includes("virtual-service")
    )?.valueBoolean === true;

  if (channel === "chat") return "chat";
  if (channel === "domicilio") return "domicilio";
  if (channel === "teleatendimento") return "teleatendimento";
  if (channel === "presencial") return "presencial";
  if (isVirtual) return "teleatendimento";
  return "presencial";
}

/** Ícone em círculo + texto do tooltip (acessível ao passar o mouse). */
const VET_ATTENDANCE_UI: Record<
  VetAttendanceKind,
  {
    icon: React.ReactElement;
    tooltip: string;
    iconColor: string;
    circleBg: string;
  }
> = {
  presencial: {
    icon: <LocationOn sx={{ fontSize: 22 }} />,
    tooltip: "Agenda presencial",
    iconColor: "primary700.main",
    circleBg: "primary100.main",
  },
  teleatendimento: {
    icon: <Videocam sx={{ fontSize: 22 }} />,
    tooltip: "Teleatendimento",
    iconColor: "secondary700.main",
    circleBg: "secondary100.main",
  },
  chat: {
    icon: <ChatIcon sx={{ fontSize: 22 }} />,
    tooltip: "Atendimento por chat",
    iconColor: "error700.main",
    circleBg: "error100.main",
  },
  domicilio: {
    icon: <Home sx={{ fontSize: 22 }} />,
    tooltip: "Atendimento a domicílio",
    iconColor: "warning700.main",
    circleBg: "warning100.main",
  },
};

/** Apresentação do status FHIR da consulta no card (borda + chips). */
const FHIR_APPOINTMENT_STATUS_UI: Record<
  string,
  { label: string; border: string; chipBg: string; chipFg: string }
> = {
  proposed: {
    label: "Proposta",
    border: "#ed6c02",
    chipBg: "#fff4e5",
    chipFg: "#b45309",
  },
  pending: {
    label: "Pendente",
    border: "#ff9800",
    chipBg: "#fff3e0",
    chipFg: "#e65100",
  },
  booked: {
    label: "Confirmada",
    border: "#00A884",
    chipBg: "#e8f5e9",
    chipFg: "#2e7d32",
  },
};

function getFhirAppointmentStatusUi(status: string | undefined) {
  if (status && FHIR_APPOINTMENT_STATUS_UI[status]) {
    return FHIR_APPOINTMENT_STATUS_UI[status];
  }
  return {
    label: status ? String(status) : "—",
    border: "#90a4ae",
    chipBg: "#eceff1",
    chipFg: "#455a64",
  };
}

/**
 * Resolve links de checkout (marketplace) para consultas pendentes com PaymentNotice ativo.
 * Executado na mesma corrida da busca de appointments para o botão "Realizar pagamento"
 * aparecer junto dos cards — evita segundo useEffect que atrasava a UI.
 */
async function resolveMarketplacePaymentLinksByAppointmentId(
  pendingAppointments: any[],
): Promise<Record<string, string>> {
  if (!pendingAppointments.length) return {};

  const entries = await Promise.all(
    pendingAppointments.map(async (apt: any) => {
      const resource = apt.appointmentResource;
      const patientParticipant = resource.participant?.find((p: any) =>
        p.actor?.reference?.startsWith("Patient/")
      );
      const slotReference = resource.slot?.[0]?.reference;

      if (!patientParticipant?.actor?.reference || !slotReference) {
        return null;
      }

      try {
        const filter = `status=active&_sort=-_lastUpdated&request=${slotReference}&response=${patientParticipant.actor.reference}&_count=1`;
        const response = await GetPaymentNotice({ filter });
        const noticeEntry = response?.data?.entry?.[0];

        if (!noticeEntry?.resource) return null;

        const isPaidNotice =
          noticeEntry.resource?.paymentStatus?.coding?.[0]?.code ===
          "cleared";
        if (isPaidNotice) return null;

        const slotId = slotReference.split("/")[1];
        if (!slotId) return null;

        const paymentLink =
          await marketplaceService.getPaymentLinkBySlotId(slotId);
        return paymentLink
          ? { id: apt.id as string, link: paymentLink }
          : null;
      } catch (error) {
        console.error(
          `Erro ao resolver link de pagamento (consulta ${apt.id}):`,
          error,
        );
        return null;
      }
    }),
  );

  const next: Record<string, string> = {};
  entries.forEach((e) => {
    if (e?.id && e.link) next[e.id] = e.link;
  });
  return next;
}

const AutoStartVideoConferencia: React.FC<{
  roomUrl: string;
  appointmentData: {
    id: string;
    patientName: string;
    practitionerName: string;
    startTime: string;
  };
  onClose?: () => void;
}> = ({ roomUrl, appointmentData, onClose }) => {
  const [hasStarted, setHasStarted] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasStarted) return;

    // Aguarda um delay para garantir que o VideoConferencia foi renderizado
    const timer = setTimeout(() => {
      // Procura o botão do VideoConferencia apenas dentro do container deste componente
      if (!containerRef.current) return;

      const allButtons = containerRef.current.querySelectorAll("button");

      for (const button of allButtons) {
        // Verifica se o botão tem um SVG com o ícone de vídeo
        const svg = button.querySelector("svg");
        if (svg) {
          // Verifica se tem o data-testid do VideoCameraFrontIcon
          const hasVideoIcon =
            svg.getAttribute("data-testid") === "VideoCameraFrontIcon";

          // Ou verifica pelo texto do botão
          const buttonText = (button.textContent || "").toLowerCase();
          const hasVideoText =
            buttonText.includes("tele") ||
            buttonText.includes("acessar") ||
            buttonText.includes("iniciar");

          if (hasVideoIcon || hasVideoText) {
            // Dispara um evento de clique real
            const event = new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              view: window,
            });
            button.dispatchEvent(event);

            // Também tenta o método click() diretamente
            (button as HTMLButtonElement).click();
            setHasStarted(true);
            break;
          }
        }
      }
    }, 500); // Delay maior para garantir renderização completa

    return () => clearTimeout(timer);
  }, [hasStarted]);

  // Observa quando a janela da videoconferência é fechada
  // Só começa a observar depois que a videoconferência foi iniciada
  useEffect(() => {
    if (!hasStarted || !onClose) return;

    let interval: NodeJS.Timeout | null = null;
    let observer: MutationObserver | null = null;

    // Aguarda um tempo antes de começar a verificar, para dar tempo da janela aparecer
    const startCheckTimer = setTimeout(() => {
      let wasWindowOpen = false;
      let checkCount = 0;

      const checkVideoWindow = () => {
        checkCount++;

        // Procura pela janela da videoconferência (WherebyComponent)
        // A janela tem z-index 99999 e contém o elemento whereby-embed
        const videoWindows = document.querySelectorAll(
          '[style*="z-index: 99999"], [style*="zIndex: 99999"]'
        );
        const hasWherebyEmbed = document.querySelector("whereby-embed");
        const isWindowOpen = videoWindows.length > 0 || !!hasWherebyEmbed;

        // Na primeira verificação, marca se a janela está aberta
        if (checkCount === 1) {
          wasWindowOpen = isWindowOpen;
          return;
        }

        // Se a janela estava aberta e agora não está mais, significa que foi fechada
        if (wasWindowOpen && !isWindowOpen) {
          onClose();
          return;
        }

        // Atualiza o estado de se a janela estava aberta
        wasWindowOpen = isWindowOpen;
      };

      // Verifica periodicamente se a janela foi fechada
      interval = setInterval(checkVideoWindow, 500);

      // Também observa mudanças no DOM para detectar quando a janela desaparece
      observer = new MutationObserver(() => {
        checkVideoWindow();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }, 1500); // Aguarda 1.5 segundos antes de começar a verificar

    // Cleanup function
    return () => {
      clearTimeout(startCheckTimer);
      if (interval) {
        clearInterval(interval);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [hasStarted, onClose]);

  // Renderiza o VideoConferencia normalmente - ele será invisível mas funcional
  return (
    <Box
      ref={containerRef}
      sx={{
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      <VideoConferencia roomUrl={roomUrl} appointmentData={appointmentData} />
    </Box>
  );
};

const VetAppointments: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState<any>([]);
  const [isPaid, setIsPaid] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [videoConferenceOpen, setVideoConferenceOpen] = useState(false);
  const [selectedVideoAppointment, setSelectedVideoAppointment] =
    useState<any>(null);

  /**
   * Links de checkout do marketplace por ID do Appointment (Fluxmed Schedule).
   * Só preenche quando há PaymentNotice ativo não quitado para o slot/paciente do pet.
   */
  const [paymentLinksByAppointmentId, setPaymentLinksByAppointmentId] =
    useState<Record<string, string>>({});

  const navigate = useNavigate();

  // Contexto para obter dados do usuário e pets
  const { user, userPatient, accessTokenGoogle } = useSessionContext();
  const { deleteEvent } = useDeleteEvent();
  const {
    patientRelated,
    loading: relatedLoading,
    fetchRelatedData,
  } = useRelatedPersons({
    identifier: user?.username,
  });

  // Estado para gerenciar as consultas
  const [appointments, setAppointments] = useState<any[]>([]);

  // Busca os pets relacionados quando o userPatient estiver disponível
  useEffect(() => {
    if (userPatient) {
      fetchRelatedData(userPatient);
    }
  }, [userPatient, fetchRelatedData]);

  // Busca appointments dos pets (dependentes)
  useEffect(() => {
    async function fetchAppointmentsDependentes() {
      // Filtra apenas pets ativos e verificados
      const auxDependents = patientRelated;

      if (!auxDependents?.length) {
        setAppointments([]);
        setPaymentLinksByAppointmentId({});
        return;
      }

      setLoading(true);

      try {
        // Define o período de busca: de hoje até 3 meses no futuro
        const startDate = useDatetime.formatDateToISO(dayjs());
        const endDate = useDatetime.formatDateToISO(dayjs().add(3, "month"));

        // Busca appointments de todos os pets em paralelo, verificando consents
        const responses = await Promise.all(
          auxDependents.map((dependente: any) => {
            const petIdentifier =
              dependente?.identifier?.[0]?.value || dependente?.id;

            if (!petIdentifier) return null;

            return GetAppointment({
              ehrrunner: petIdentifier,
              startDate,
              endDate,

              status: ["booked", "pending", "proposed"],
              sort: "date",
              includeLocation: true,
              includePatient: true,
            }).catch((error) => {
              console.error(
                `Erro ao buscar appointments do pet ${petIdentifier}:`,
                error
              );
              return null;
            });
          })
        );

        // Processa cada resposta do bundle separadamente para manter a relação appointment-pet
        const formattedAppointments: any[] = [];

        responses.forEach((bundle) => {
          if (!bundle?.entry) return;

          // Separa entries por tipo: Appointment, Patient, Location
          const appointmentsEntries = bundle.entry.filter(
            (entry: any) => entry?.resource?.resourceType === "Appointment"
          );
          const patientsEntries = bundle.entry.filter(
            (entry: any) => entry?.resource?.resourceType === "Patient"
          );
          const locationsEntries = bundle.entry.filter(
            (entry: any) => entry?.resource?.resourceType === "Location"
          );

          // Processa cada appointment
          appointmentsEntries.forEach((entry: any) => {
            const appointment = entry.resource;

            // Encontra o paciente (pet) relacionado ao appointment
            const petParticipant = appointment.participant?.find((p: any) =>
              p.actor?.reference?.includes("Patient")
            );

            let petName = "Pet";
            if (petParticipant) {
              const petId = petParticipant.actor?.reference?.split("/")[1];

              // Tenta encontrar o pet nas entries do bundle primeiro
              const petEntry = patientsEntries.find(
                (e: any) => e.resource?.id === petId
              );

              if (petEntry) {
                petName = petEntry.resource?.name?.[0]?.text || "Pet";
              } else {
                // Se não encontrou no bundle, busca nos dependentes
                const pet = auxDependents.find(
                  (p: any) =>
                    p.id === petId || p.identifier?.[0]?.value === petId
                );
                petName = pet?.name?.[0]?.text || "Pet";
              }
            }

            // Encontra a location relacionada
            const locationParticipant = appointment.participant?.find(
              (p: any) => p.actor?.reference?.includes("Location")
            );
            const locationId =
              locationParticipant?.actor?.reference?.split("/")[1];
            const locationEntry = locationsEntries.find(
              (e: any) => e.resource?.id === locationId
            );

            // Extrai dados do appointment usando a função helper
            const appointmentData = useFhirData.extractAppointmentData(
              entry,
              patientsEntries.find(
                (e: any) =>
                  e.resource?.id ===
                  petParticipant?.actor?.reference?.split("/")[1]
              ),
              locationEntry
            );

            // Formata a data e hora
            const startDateTime = dayjs(appointment.start);
            const date = startDateTime.format("YYYY-MM-DD");
            const time = startDateTime.format("HH:mm");

            // Extrai o virtualService (URL da videoconferência) do appointment
            const virtualServiceUrl =
              appointment.virtualService?.[0]?.addressUrl || null;

            // Extrai o canal de atendimento da Location (chat, domicilio, teleatendimento…)
            const attendanceChannel =
              locationEntry?.resource?.extension?.find((ext: any) =>
                ext.url?.includes("attendance-channel")
              )?.valueCode || null;

            // Classificação única para ícone + tooltip (presencial / tele / chat / domicílio)
            const attendanceKind = getVetAttendanceKind(
              locationEntry?.resource
            );

            formattedAppointments.push({
              id: appointment.id,
              doctor: appointmentData.practitioner || "Veterinário",
              specialty: appointmentData.healthcare || "Clínica Veterinária",
              date: date,
              time: time,
              clinic: appointmentData.location || "Não informado",
              pet: petName,
              /** Status FHIR bruto para chips e cores do card */
              fhirStatus: appointment.status,
              startTime: appointment.start,
              // Armazena o appointment completo para cancelamento
              appointmentResource: appointment,
              // URL da videoconferência (se houver)
              virtualService: virtualServiceUrl,
              // Canal de atendimento da location (chat | domicilio | teleatendimento | null)
              attendanceChannel,
              /** Tipo de agenda para ícone + tooltip no card */
              attendanceKind,
            });
          });
        });

        // Filtra apenas appointments futuros, ordena por data e limita a 6 próximos
        const finalAppointments = formattedAppointments
          .filter((apt: any) =>
            dayjs(apt.startTime).isAfter(dayjs().subtract(1, "day"))
          )
          .sort((a: any, b: any) => dayjs(a.startTime).diff(dayjs(b.startTime)))
          .slice(0, 6);

        const pendingForPayment = finalAppointments.filter(
          (apt: any) => apt.appointmentResource?.status === "pending",
        );
        const paymentLinksMap =
          pendingForPayment.length > 0
            ? await resolveMarketplacePaymentLinksByAppointmentId(
                pendingForPayment,
              )
            : {};

        setAppointments(finalAppointments);
        setPaymentLinksByAppointmentId(paymentLinksMap);
      } catch (error) {
        console.error("Erro ao buscar appointments dos pets:", error);
        setAppointments([]);
        setPaymentLinksByAppointmentId({});
      } finally {
        setLoading(false);
      }
    }

    if (patientRelated?.length) {
      fetchAppointmentsDependentes();
    } else {
      setAppointments([]);
      setPaymentLinksByAppointmentId({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientRelated, refetch]);

  // Busca payment notice quando um appointment é selecionado para cancelamento
  useEffect(() => {
    async function fetchPayment() {
      if (selectedAppointment?.appointmentResource?.id) {
        const appointment = selectedAppointment.appointmentResource;

        // Encontra o participant que é o paciente
        const patientParticipant = appointment.participant?.find((p: any) =>
          p.actor?.reference?.startsWith("Patient/")
        );

        // Encontra o slot no appointment
        const slotReference = appointment.slot?.[0]?.reference;

        if (patientParticipant?.actor?.reference && slotReference) {
          try {
            const filter = `status=active&_sort=-_lastUpdated&request=${slotReference}&response=${patientParticipant.actor.reference}&_count=1`;
            const response = await GetPaymentNotice({ filter });
            setPaymentNotice(response?.data?.entry || []);

            // Verifica se existe paymentStatus no PaymentNotice
            const hasPaymentStatus =
              response?.data?.entry?.[0]?.resource?.paymentStatus?.coding?.[0]
                ?.code === "cleared";
            setIsPaid(!!hasPaymentStatus);
          } catch (error) {
            console.error("Erro ao buscar PaymentNotice:", error);
            setPaymentNotice([]);
            setIsPaid(false);
          }
        } else {
          setPaymentNotice([]);
          setIsPaid(false);
        }
      }
    }

    if (cancelDialogOpen && selectedAppointment) {
      fetchPayment();
    }
  }, [cancelDialogOpen, selectedAppointment]);

  // Função para abrir o dialog de cancelamento
  const handleOpenCancelDialog = (appointment: any) => {
    setSelectedAppointment(appointment);
    setCancelDialogOpen(true);
  };

  // Função para fechar o dialog de cancelamento
  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedAppointment(null);
    setPaymentNotice([]);
    setIsPaid(false);
  };

  // Função para cancelar a consulta
  const handleCancelAppointment = async () => {
    if (isPaid) {
      toast.error("Não é possível cancelar uma consulta já paga");
      return;
    }

    if (!selectedAppointment?.appointmentResource?.id) {
      toast.error("Erro ao identificar a consulta");
      return;
    }

    setCancelLoading(true);
    try {
      const appointment = selectedAppointment.appointmentResource;

      // Cancela o PaymentNotice se houver
      if (paymentNotice?.length > 0) {
        const paymentCopy = { ...paymentNotice[0]?.resource };
        paymentCopy.status = "cancelled";
        await PutPaymentNotice({
          paymentId: paymentNotice[0]?.resource?.id,
          data: paymentCopy,
        });
      }

      // Remove do Google Calendar se houver
      const googleCalendarExt = appointment?.extension?.find((ext: any) =>
        ext.url.includes("googleCalendarId")
      );

      if (
        googleCalendarExt?.valueString &&
        accessTokenGoogle &&
        user?.refreshTokenGoogle
      ) {
        try {
          await deleteEvent({
            eventId: googleCalendarExt.valueString,
            accessTokenGoogle,
            refreshToken: user.refreshTokenGoogle,
          });
        } catch (err) {
          console.error("Erro ao excluir evento do Google Calendar:", err);
        }
      }

      // Cancela o Appointment
      await PatchAppointmentStatus({
        appointmentId: appointment.id,
        status: "cancelled",
      });

      // Remove do Whereby se houver
      if (appointment?.virtualService) {
        DeleteWhereby(appointment?.virtualService?.[0]?.sessionKey as string);
      }

      toast.success("Consulta cancelada com sucesso!");
      setRefetch((prev) => !prev);
      handleCloseCancelDialog();
    } catch (error) {
      console.error("Erro ao cancelar consulta:", error);
      toast.error("Erro ao cancelar consulta");
    } finally {
      setCancelLoading(false);
    }
  };

  const daysUntilAppointment = (dateString: string) => {
    const today = dayjs();
    const appointmentDate = dayjs(dateString);
    const days = appointmentDate.diff(today, "day");

    if (days === 0) return "Hoje";
    if (days === 1) return "Amanhã";
    return `Em ${days} dias`;
  };

  /**
   * Data/hora compactas em pt-BR (card pequeno): dia da semana abreviado + data curta + hora.
   */
  const describeAppointmentInstant = (iso: string) => {
    const d = dayjs(iso).locale("pt-br");
    return {
      /** Ex.: "qui., 15/05/2026" */
      dateCompact: d.format("ddd, DD/MM/YYYY"),
      timeLabel: d.format("HH:mm"),
    };
  };

  // Exibe loading enquanto busca dados ou appointments
  if (loading || relatedLoading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CalendarIcon sx={{ mr: 1, color: "#00A884" }} />
          <Typography variant="h6">Próximas Consultas</Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Event />}
          onClick={() => {
            navigate("/schedulingsearch");
          }}
        >
          Agendar Consulta
        </Button>
      </Box>

      {appointments.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Você não tem consultas agendadas.
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              navigate("/schedulingsearch");
            }}
            startIcon={<Event />}
            sx={{
              mt: 2,
              bgcolor: "#00A884",
              "&:hover": { bgcolor: "#008c6e" },
            }}
          >
            Agendar Consulta
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {appointments.map((appointment) => {
            const appointmentWhen = describeAppointmentInstant(
              appointment.startTime
            );
            const attendanceKey =
              (appointment.attendanceKind as VetAttendanceKind) || "presencial";
            const attendanceUi =
              VET_ATTENDANCE_UI[attendanceKey] ?? VET_ATTENDANCE_UI.presencial;
            const statusUi = getFhirAppointmentStatusUi(
              appointment.fhirStatus
            );
            // Enquanto houver fluxo de pagamento no card, não exibir chat/teleatendimento
            const showPaymentButton =
              !!paymentLinksByAppointmentId[appointment.id];

            return (
              <Grid item xs={12} sm={6} md={4} key={appointment.id}>
                <Card
                  sx={{
                    height: "100%",
                    borderLeft: `4px solid ${statusUi.border}`,
                    boxShadow: 1,
                    // Sem translateY no hover: o movimento do card quebrava o hit-test dos botões / chat,
                    // gerando piscadas ao mover o mouse na área inferior do card.
                    transition: "box-shadow 0.2s ease",
                    "&:hover": {
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent
                    sx={{ pt: 2, pb: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}
                  >
                    {/* Profissional + especialidade + status; tipo de agenda no ícone (mantido) */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {appointment.doctor}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.25 }}
                        >
                          {appointment.specialty}
                        </Typography>
                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          gap={0.75}
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Chip
                            label={statusUi.label}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              bgcolor: statusUi.chipBg,
                              color: statusUi.chipFg,
                            }}
                          />
                          <Chip
                            label={daysUntilAppointment(appointment.date)}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              borderColor: statusUi.border,
                              color: statusUi.chipFg,
                            }}
                          />
                        </Stack>
                      </Box>
                      <Tooltip title={attendanceUi.tooltip} arrow describeChild>
                        <Box
                          component="span"
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            bgcolor: attendanceUi.circleBg,
                            color: attendanceUi.iconColor,
                            border: "1px solid",
                            borderColor: "divider",
                            cursor: "default",
                          }}
                        >
                          {attendanceUi.icon}
                        </Box>
                      </Tooltip>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Data, hora e local compactos */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.75,
                        rowGap: 0.5,
                        mb: 1,
                      }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: 18, color: "text.secondary" }}
                      />
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontWeight: 600, textTransform: "capitalize" }}
                      >
                        {appointmentWhen.dateCompact}
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        color="text.secondary"
                      >
                        ·
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      >
                        {appointmentWhen.timeLabel}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                        mb: 0.75,
                      }}
                    >
                      <LocationCity
                        sx={{
                          fontSize: 18,
                          color: "text.secondary",
                          flexShrink: 0,
                          mt: 0.15,
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.35 }}
                      >
                        {appointment.clinic}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        mb: 1,
                      }}
                    >
                      <Pets sx={{ fontSize: 18, color: "#00A884" }} />
                      <Typography variant="body2" color="text.secondary">
                        Pet{" "}
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "text.primary" }}
                        >
                          {appointment.pet}
                        </Box>
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {/* Pagamento — como na primeira versão (Schedule): ícone + texto dentro do botão */}
                      {showPaymentButton ? (
                        <Tooltip title="Realizar pagamento">
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                backgroundColor: "neutral600.main",
                                color: "neutral0.main",
                                "&:hover": {
                                  backgroundColor: "neutral700.main",
                                },
                                whiteSpace: "nowrap",
                                minWidth: "fit-content",
                                height: 36,
                                borderRadius: 0,
                                textTransform: "uppercase",
                                fontWeight: 500,
                                gap: 0.5,
                              }}
                              onClick={() => {
                                const paymentLink =
                                  paymentLinksByAppointmentId[appointment.id];
                                if (paymentLink) {
                                  window.open(paymentLink, "_blank");
                                }
                              }}
                            >
                              <AttachMoney fontSize="small" />
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{ textTransform: "uppercase", fontWeight: 500 }}
                              >
                                Realizar pagamento
                              </Typography>
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}

                      {/* Chat e tele são mutuamente exclusivos (chat tem prioridade). Pagamento oculta ambos. */}
                      {!showPaymentButton &&
                        (appointment.attendanceChannel === "chat" ? (
                          <ChatMedPaciente
                            role="patient"
                            cardMode={false}
                            fluxVetCardActions
                          />
                        ) : appointment.virtualService ? (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 36,
                              borderRadius: 0,
                              textTransform: "uppercase",
                              fontWeight: 500,
                              borderColor: "#00A884",
                              color: "#00A884",
                              "&:hover": {
                                borderColor: "#008c6e",
                                bgcolor: "rgba(0, 168, 132, 0.04)",
                              },
                            }}
                            onClick={() => {
                              setSelectedVideoAppointment(appointment);
                              setVideoConferenceOpen(true);
                            }}
                          >
                            Iniciar teleatendimento
                          </Button>
                        ) : null)}

                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        sx={{
                          height: 36,
                          borderRadius: 0,
                          textTransform: "uppercase",
                          fontWeight: 500,
                        }}
                        onClick={() =>
                          handleOpenCancelDialog(appointment)
                        }
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Dialog de confirmação de cancelamento */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle fontWeight={600} textAlign="center">
          Cancelar consulta
        </DialogTitle>
        <DialogContent>
          {isPaid ? (
            <Stack alignItems="center" spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <AttachMoney sx={{ color: "warning.main", fontSize: 32 }} />
                <Typography variant="h6" color="warning.main">
                  Consulta já paga
                </Typography>
              </Stack>
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
              >
                Não é possível cancelar uma consulta com pagamento já
                confirmado.
              </Typography>
            </Stack>
          ) : (
            <Typography textAlign="center" variant="subtitle2">
              Deseja confirmar o cancelamento desta consulta?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseCancelDialog}
            disabled={cancelLoading}
            color="inherit"
          >
            Não
          </Button>
          <Button
            onClick={handleCancelAppointment}
            disabled={cancelLoading || isPaid}
            variant="contained"
            color="error"
            sx={{
              bgcolor: "#f44336",
              "&:hover": { bgcolor: "#d32f2f" },
            }}
          >
            {cancelLoading ? <CircularProgress size={20} /> : "Sim, cancelar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VideoConferencia para teleatendimento - inicia automaticamente quando renderizado */}
      {videoConferenceOpen && selectedVideoAppointment?.virtualService && (
        <AutoStartVideoConferencia
          roomUrl={selectedVideoAppointment.virtualService}
          appointmentData={{
            id: selectedVideoAppointment.id,
            patientName: selectedVideoAppointment.pet,
            practitionerName: selectedVideoAppointment.doctor,
            startTime: selectedVideoAppointment.startTime,
          }}
          onClose={() => {
            // Reseta os estados quando a videoconferência é fechada
            setVideoConferenceOpen(false);
            setSelectedVideoAppointment(null);
          }}
        />
      )}
    </Paper>
  );
};

export default VetAppointments;
